import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

export default function LoginPage() {
    const { signIn, authError, clearAuthError } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Show authError from context (e.g., unauthorized access)
    useEffect(() => {
        if (authError) {
            setError(authError);
            clearAuthError();
        }
    }, [authError, clearAuthError]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        setLoading(true);

        console.log('LoginPage handleSubmit called, signIn function exists:', !!signIn);
        try {
            console.log('Calling signIn from LoginPage...');
            await signIn(email, password);
            console.log('signIn completed successfully, navigating to dashboard');
            // Navigate to dashboard after successful login
            navigate('/');
        } catch (err: any) {
            console.error('signIn failed in LoginPage:', err);
            setError(err.message || 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-md w-full space-y-8 p-8 bg-surface rounded-lg shadow-md border border-border">
                {tenant?.logoUrl && (
                    <div className="flex justify-center">
                        <img src={tenant.logoUrl} alt={tenant.name} className="h-12" />
                    </div>
                )}

                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
                        Sign in to your account
                    </h2>
                    <p className="mt-2 text-center text-sm text-text-secondary">
                        {tenant?.name || 'Stellar Bingo'}
                    </p>
                </div>
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-start gap-3">
                            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <input
                                type="email"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm bg-surface"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <input
                                type="password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-border placeholder-gray-500 text-text-primary rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm bg-surface"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full"
                    >
                        {loading ? 'Signing in...' : 'Sign in'}
                    </button>

                    <p className="text-center text-sm text-text-secondary">
                        Don't have an account?{' '}
                        <Link to="/signup" className="text-primary hover:underline font-medium">
                            Create one
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

